import * as THREE from 'three';
import type { PhysicsCity } from '../physics/world';
import type { City, Building } from '../world/city';
import { personGeometry } from '../render/geometry';
import type { Lifecycle } from '../core/lifecycle';
import type { AudioSystem } from './audio';
import type { Vec } from '../destruction/structure';

/** Presentation follows actual restoration. Skip removes pacing only. */
export class Rebuild {
  workers: THREE.InstancedMesh;
  loaded = false;
  focus: () => Vec = () => ({ x: 0, y: 0, z: 0 });
  private sites: Building[];
  private dummy = new THREE.Object3D();
  private lastBeat = -1;
  private skip = false;
  private prepared = false;
  onClean: (incremental?: boolean) => void = () => {};
  onPrepare: (budgetMs: number) => boolean = () => true;
  onFinish: () => void = () => {};
  profile = { epoch: 0, started: 0, finished: 0, maxSliceMs: 0, slices: 0, skipAt: 0 };
  constructor(
    readonly physics: PhysicsCity,
    readonly city: City,
    readonly life: Lifecycle,
    readonly audio: AudioSystem,
  ) {
    const chunks = new Map<string, Building[]>();
    for (const b of city.buildings) {
      const key = b.chunk ?? `${Math.floor(b.x / 100)},${Math.floor(b.z / 100)}`;
      const group = chunks.get(key) ?? [];
      group.push(b);
      chunks.set(key, group);
    }
    this.sites = [...chunks.values()].flatMap((group) => group.slice(0, 2));
    this.workers = new THREE.InstancedMesh(
      personGeometry(true),
      physics.material,
      Math.max(1, this.sites.length),
    );
    this.workers.count = 0;
    this.workers.castShadow = true;
    this.workers.frustumCulled = false;
    physics.scene.add(this.workers);
  }
  begin(): void {
    if (this.life.phase === 'rebuilding') return;
    this.life.begin();
    this.loaded = this.skip = this.prepared = false;
    this.lastBeat = -1;
    this.profile = {
      epoch: this.life.epoch,
      started: performance.now(),
      finished: 0,
      maxSliceMs: 0,
      slices: 0,
      skipAt: 0,
    };
    this.workers.count = this.sites.length;
    this.physics.reconstruction = undefined;
    this.physics.reconstructionStageLimit = -1;
  }
  get progress(): number {
    const r = this.physics.reconstruction;
    if (this.prepared) return 1;
    return r?.totalCells ? Math.min(0.96, (r.cells / r.totalCells) * 0.9 + (r.phase >= 4 ? 0.04 : 0)) : 0;
  }
  get status(): string {
    const phase = this.physics.reconstruction?.phase ?? -1;
    return (
      (this.skip ? 'Fast restore · ' : '') +
      [
        'Clearing and sorting.',
        'Foundations and roads.',
        'Frames and floors.',
        'Facades and roofs.',
        'Glass, interiors and details.',
        'Routes, occupants and activity.',
      ][phase + 1]
    );
  }
  update(dt: number): void {
    if (this.life.phase !== 'rebuilding') return;
    this.life.elapsed += dt;
    const t = this.life.elapsed;
    // Let the acknowledgement render before any disposal or restoration work.
    if (!this.loaded && (t >= 0.08 || this.skip)) {
      this.loaded = true;
      this.onClean(true);
    }
    this.physics.reconstructionStageLimit = this.skip
      ? 4
      : t < 1.1
        ? 0
        : t < 2.6
          ? 1
          : t < 4.1
            ? 2
            : t < 5.4
              ? 3
              : 4;
    if (this.loaded && !this.prepared) {
      const start = performance.now();
      // A fixed 6ms per rendered frame starved real restoration in slower
      // compatibility renderers. Use at most half the previous frame, capped
      // at 12ms; each slice still yields for input and visible reconstruction.
      this.prepared = this.onPrepare(Math.min(12, Math.max(6, dt * 500)));
      this.profile.maxSliceMs = Math.max(this.profile.maxSliceMs, performance.now() - start);
      this.profile.slices++;
    }
    this.sites.forEach((b, i) => {
      const phase = this.physics.reconstruction?.phase ?? -1;
      const travel = Math.min(1, t * 2),
        lift = phase === 1 ? Math.sin(t * 6 + i) * 0.12 : 0;
      this.dummy.position.set(
        b.x + Math.min(b.w * 0.625, 3) + Math.sin(t * 2 + i) * 0.12,
        (b.baseY ?? 0.16) + lift,
        b.z - 3.8 + travel,
      );
      this.dummy.rotation.set(
        phase >= 0 ? Math.sin(t * 9 + i) * 0.12 : 0,
        0.1 + Math.sin(t * 3 + i) * 0.2,
        Math.sin(t * 10 + i) * 0.06,
      );
      this.dummy.scale.setScalar(this.prepared ? 0 : Math.min(1, t * 5));
      this.dummy.updateMatrix();
      this.workers.setMatrixAt(i, this.dummy.matrix);
    });
    this.workers.instanceMatrix.needsUpdate = true;
    if (Math.floor(t * 3) !== this.lastBeat && this.loaded && !this.prepared) {
      this.audio.play('build', 0.4);
      this.lastBeat = Math.floor(t * 3);
    }
    if (this.prepared && (this.skip || t >= 7)) {
      this.workers.count = 0;
      this.physics.reconstructionStageLimit = Infinity;
      this.profile.finished = performance.now();
      this.life.finish();
      this.onFinish();
    }
  }
  finish(): void {
    if (this.life.phase !== 'rebuilding') return;
    this.skip = true;
    this.profile.skipAt ||= performance.now();
  }
  instant(): void {
    if (this.life.phase !== 'rebuilding') this.begin();
    this.finish();
  }
}
